import React, { useState, useCallback, useMemo } from 'react';
import Header from './components/Header/Header';
import ImageUploader from './components/ImageUploader/ImageUploader';
import PlantCanvas from './components/PlantCanvas/PlantCanvas';
import ControlPanel from './components/ControlPanel/ControlPanel';
import JsOutput from './components/JsOutput/JsOutput';
import NameGroupModal from './components/NameGroupModal/NameGroupModal';
import './App.css';

import { extractConfigFromJsFile, sapToEditor } from './utils/cmdConfig';
import { buildJsExport } from './utils/exportJs';

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const App = () => {
  const [currentImage, setCurrentImage] = useState(null);
  const [lightPoints, setLightPoints] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarView, setSidebarView] = useState('controls');
  const [pendingGroup, setPendingGroup] = useState(null);

  // Configuração atual para novos elementos
  const [currentConfig, setCurrentConfig] = useState({
    lightType: 'normal',     // 'normal' ou 'ledStrip' (ferramenta selecionada)
    moduleType: '4',
    maxIdx: 4,
    descricao: '',
    modulo: '1',
    idx: '1',
    circleNumber: 1,
    width: 2.5,
    height: 5,
    ledColor: '#f4bb00',
    ledMode: 'static',
    ledStrokeWidth: '2.5'
  });

  // Handlers para imagem
  const handleImageLoad = useCallback((imageUrl, metadata) => {
    if (imageUrl) {
      setCurrentImage({
        url: imageUrl,
        metadata: metadata
      });
    } else {
      setCurrentImage(null);
      setLightPoints([]);
      setSelectedPointId(null);
    }
  }, []);

  // Handler para adicionar elementos (ponto ou led)
  const handleAddLightPoint = useCallback(
    (newPoint) => {
      // Descobrir se é LED ou LIGHT pelo tool atual
      const isLed = currentConfig.lightType === 'ledStrip';

      // tenta achar grupo existente pelo módulo+idx+circleNumber
      const existingGroup = lightPoints.find(
        (group) =>
          group.modulo === currentConfig.modulo &&
          group.idx === currentConfig.idx &&
          group.circleNumber === currentConfig.circleNumber
      );

      if (existingGroup) {
        // adiciona nova instância ao grupo existente
        setLightPoints((prev) =>
          prev.map((group) => {
            if (group.id !== existingGroup.id) return group;

            const instances = group.instances || [];
            const newInstanceIndex = instances.length;

            const newInstance = isLed
              ? {
                  tipo: 'led',
                  x: 0,
                  y: 0,
                  width: 100,
                  height: 100,
                  ledPath: newPoint.ledPath, // vindo do LedStripEditor
                  ledColor: currentConfig.ledColor || '#f4bb00',
                  ledMode: currentConfig.ledMode || 'static',
                  ledStrokeWidth: Number(currentConfig.ledStrokeWidth) || 0.25
                }
              : {
                  tipo: 'light',
                  x: Math.round(newPoint.x * 10) / 10,
                  y: Math.round(newPoint.y * 10) / 10,
                  width: currentConfig.width || 2.5,
                  height: currentConfig.height || 5,
                  circleNumber: group.circleNumber
                };

            const newId = `${group.id}_${newInstanceIndex}`;
            setSelectedPointId(newId);

            return {
              ...group,
              instances: [...instances, newInstance]
            };
          })
        );
      } else {
        // === PRIMEIRO PONTO DESSE CIRCUITO -> abre modal ===
        const groupNumber = String(currentConfig.circleNumber).padStart(2, '0');
        const defaultDescricao = `Circuito ${groupNumber}`;

        const initialDescricao = defaultDescricao;

        setPendingGroup({
          point: newPoint,
          configSnapshot: { ...currentConfig },
          groupNumber,
          defaultDescricao,
          initialDescricao
        });
      }
    },
    [lightPoints, currentConfig]
  );

  const handleConfirmGroupName = useCallback(
  (finalDescricao) => {
    if (!pendingGroup) return;

    const {
      point,
      configSnapshot,
      groupNumber,
      defaultDescricao
    } = pendingGroup;

    const isLed = configSnapshot.lightType === 'ledStrip';
    const newGroupId = `grupo${groupNumber}`;
    const descricaoToUse = (finalDescricao || '').trim() || defaultDescricao;

    const firstInstance = isLed
      ? {
          tipo: 'led',
          x: 0,
          y: 0,
          width: 100,
          height: 100,
          ledPath: point.ledPath,
          ledColor: configSnapshot.ledColor || '#f4bb00',
          ledMode: configSnapshot.ledMode || 'static',
          ledStrokeWidth: Number(configSnapshot.ledStrokeWidth) || 0.25
        }
      : {
          tipo: 'light',
          x: Math.round(point.x * 10) / 10,
          y: Math.round(point.y * 10) / 10,
          width: configSnapshot.width || 2.5,
          height: configSnapshot.height || 5,
          circleNumber: configSnapshot.circleNumber
        };

    const newGroup = {
      id: newGroupId,
      modulo: configSnapshot.modulo,
      idx: configSnapshot.idx,
      descricao: descricaoToUse,
      circleNumber: configSnapshot.circleNumber,
      instances: [firstInstance]
    };

    setLightPoints((prev) => [...prev, newGroup]);
    setSelectedPointId(`${newGroupId}_0`);
    setPendingGroup(null);
  },
  [pendingGroup]
  );

const handleCancelGroupName = useCallback(() => {
  // Apenas fecha o modal e não cria o grupo / ponto
  setPendingGroup(null);
}, []);

  const handleSelectLightPoint = useCallback((pointId) => {
    setSelectedPointId(pointId);
  }, []);

  const handleDeleteLightPoint = useCallback(
    (pointId) => {
      if (pointId.includes('_')) {
        const [groupId, instanceIndexStr] = pointId.split('_');
        const idx = parseInt(instanceIndexStr, 10);

        setLightPoints((prev) =>
          prev
            .map((group) => {
              if (group.id !== groupId || !group.instances) return group;

              const newInstances = group.instances.filter((_, i) => i !== idx);

              if (newInstances.length === 0) {
                // remove o grupo inteiro
                return null;
              }

              return { ...group, instances: newInstances };
            })
            .filter(Boolean)
        );

        if (selectedPointId === pointId) {
          setSelectedPointId(null);
        }
      } else {
        // fallback: deletar grupo inteiro se vier só "grupo01"
        setLightPoints((prev) => prev.filter((group) => group.id !== pointId));
        if (selectedPointId === pointId) {
          setSelectedPointId(null);
        }
      }
    },
    [selectedPointId]
  );

  const handleClearAllLights = useCallback(() => {
    const totalInstances = lightPoints.reduce(
      (sum, group) => sum + (group.instances?.length || 0),
      0
    );

    if (totalInstances === 0) return;

    if (
      confirm(
        `Tem certeza que deseja remover todas as ${totalInstances} instâncias (pontos + fitas LED)?`
      )
    ) {
      setLightPoints([]);
      setSelectedPointId(null);

      setCurrentConfig((prev) => ({
        ...prev,
        modulo: '1',
        idx: '1',
        circleNumber: 1
      }));
    }
  }, [lightPoints]);

  // Handler para mudanças na configuração
  const handleConfigChange = useCallback((newConfig) => {
    setCurrentConfig(newConfig);
  }, []);

  // Handlers para controles rápidos
  const handleQuickAdvanceNumber = () => {
    const nextCircleNumber = currentConfig.circleNumber + 1;
    const moduleType = parseInt(currentConfig.moduleType || '4');

    const slotIndex = nextCircleNumber - 1; // Base 0
    const nextModulo = Math.floor(slotIndex / moduleType) + 1;
    const nextIdx = (slotIndex % moduleType) + 1;

    setCurrentConfig((prev) => ({
      ...prev,
      circleNumber: nextCircleNumber,
      modulo: String(nextModulo),
      idx: String(nextIdx)
    }));
  };

  const handleQuickResetNumber = () => {
    if (currentConfig.circleNumber > 1) {
      const prevCircleNumber = currentConfig.circleNumber - 1;
      const moduleType = parseInt(currentConfig.moduleType || '4');

      const slotIndex = prevCircleNumber - 1; // Base 0
      const prevModulo = Math.floor(slotIndex / moduleType) + 1;
      const prevIdx = (slotIndex % moduleType) + 1;

      setCurrentConfig((prev) => ({
        ...prev,
        circleNumber: prevCircleNumber,
        modulo: String(prevModulo),
        idx: String(prevIdx)
      }));
    }
  };

  const handleQuickNextSlot = () => {
    const moduleType = parseInt(currentConfig.moduleType || '4');
    const currentIdx = parseInt(currentConfig.idx);
    const currentModule = parseInt(currentConfig.modulo);

    let nextIdx = currentIdx;
    let nextModule = currentModule;

    if (currentIdx >= moduleType) {
      nextIdx = 1;
      nextModule = currentModule + 1;
    } else {
      nextIdx = currentIdx + 1;
    }

    setCurrentConfig((prev) => ({
      ...prev,
      idx: String(nextIdx),
      modulo: String(nextModule)
    }));
  };

  const handleChangeTool = useCallback((tool) => {
    setCurrentConfig((prev) => ({ ...prev, lightType: tool }));
  }, []);

  const handleUpdateGroupDescription = useCallback((groupId, newDescricao) => {
    setLightPoints(prev =>
      prev.map(group =>
        group.id === groupId
          ? { ...group, descricao: newDescricao }
          : group
      )
    );
  }, []);

  const handleUpdateGroupConfig = useCallback((groupId, updates) => {
    setLightPoints(prev =>
      prev.map(group =>
        group.id === groupId
          ? { ...group, ...updates }
          : group
      )
    );
  }, []);

    const handleUpdateInstanceConfig = useCallback((instanceId, updates) => {
    if (!instanceId) return;

    setLightPoints(prev => {
      const next = [...prev];

      const [groupId, instIndexStr] = instanceId.split('_');
      const instIndex = parseInt(instIndexStr, 10);
      if (Number.isNaN(instIndex)) return prev;

      const groupIndex = next.findIndex(g => g.id === groupId);
      if (groupIndex === -1) return prev;

      const group = next[groupIndex];
      const instances = [...(group.instances || [])];
      if (!instances[instIndex]) return prev;

      let inst = { ...instances[instIndex] };

      // Atualizações simples: x, y, width, height
      if (updates.x !== undefined) inst.x = updates.x;
      if (updates.y !== undefined) inst.y = updates.y;
      if (updates.width !== undefined) inst.width = updates.width;
      if (updates.height !== undefined) inst.height = updates.height;

      const hasCircleChange = updates.circleNumber !== undefined;

      if (!hasCircleChange) {
        // Só mexeu em geometria → fica no mesmo grupo
        instances[instIndex] = inst;
        next[groupIndex] = { ...group, instances };
        return next;
      }

      // === Mudança de Número do Grupo (circleNumber) → move a instância de grupo ===
      const newCircleNumber = Math.max(1, parseInt(updates.circleNumber) || 1);
      inst.circleNumber = newCircleNumber;

      // Remove instância do grupo atual
      instances.splice(instIndex, 1);
      if (instances.length === 0) {
        // remove o grupo se ficou vazio
        next.splice(groupIndex, 1);
      } else {
        next[groupIndex] = { ...group, instances };
      }

      // Calcula módulo/idx baseado no número do grupo (regra que você já usava)
      const moduleType = parseInt(currentConfig.moduleType || '4');
      const slotIndex = newCircleNumber - 1;
      const newModulo = Math.floor(slotIndex / moduleType) + 1;
      const newIdx = (slotIndex % moduleType) + 1;

      const newGroupId = `grupo${String(newCircleNumber).padStart(2, '0')}`;

      // Verifica se já existe esse grupo
      const targetIndex = next.findIndex(g => g.id === newGroupId);

      if (targetIndex !== -1) {
        const target = next[targetIndex];
        next[targetIndex] = {
          ...target,
          circleNumber: newCircleNumber,
          modulo: String(newModulo),
          idx: String(newIdx),
          instances: [...(target.instances || []), inst]
        };
      } else {
        const groupNumberStr = String(newCircleNumber).padStart(2, '0');
        const defaultDescricao = `Circuito ${groupNumberStr}`;

        next.push({
          id: newGroupId,
          modulo: String(newModulo),
          idx: String(newIdx),
          descricao: defaultDescricao,
          circleNumber: newCircleNumber,
          instances: [inst]
        });
      }

      return next;
    });

    if (updates.circleNumber !== undefined) {
      // evita ficar com seleção apontando pro old id/index
      setSelectedPointId(null);
    }
  }, [currentConfig.moduleType]);

  // Elemento selecionado (ponto ou led)
  const selectedPoint = useMemo(() => {
    if (!selectedPointId) return null;

    const [groupId, instanceIndexStr] = selectedPointId.split('_');
    const idx = parseInt(instanceIndexStr, 10);
    if (Number.isNaN(idx)) return null;

    const group = lightPoints.find((g) => g.id === groupId);
    if (!group || !group.instances || !group.instances[idx]) return null;

    const inst = group.instances[idx];

    return {
      ...inst,
      id: `${group.id}_${idx}`,
      groupId: group.id,
      modulo: group.modulo,
      idx: group.idx,
      descricao: group.descricao
    };
  }, [selectedPointId, lightPoints]);

  // Handlers da sidebar
  const openSidebar = (view = 'controls') => {
    setSidebarView(view);
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Função para converter elementos para renderização no canvas
  const getElementsForCanvas = () => {
    const canvasElements = [];

    lightPoints.forEach((group) => {
      (group.instances || []).forEach((instance, index) => {
        const isLed = instance.tipo === 'led';

        canvasElements.push({
          id: `${group.id}_${index}`,
          groupId: group.id,
          x: instance.x,
          y: instance.y,
          width: instance.width,
          height: instance.height,
          circleNumber: instance.circleNumber,
          descricao: group.descricao,
          tipo: instance.tipo,
          isLedStrip: isLed,
          ledPath: isLed ? instance.ledPath : undefined,
          ledColor: isLed ? instance.ledColor : undefined,
          ledMode: isLed ? instance.ledMode : undefined,
          ledStrokeWidth: isLed ? instance.ledStrokeWidth : undefined
        });
      });
    });

    return canvasElements;
  };

  // Exportar configuração (JS único)
  const handleExportConfig = () => {
    const totalInstances = lightPoints.reduce(
      (sum, group) => sum + (group.instances?.length || 0),
      0
    );

    if (totalInstances === 0) {
      alert('Não há pontos para exportar!');
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    const { jsString } = buildJsExport(lightPoints, 'iluminacaoConfig');

    if (!jsString) {
      alert('Não foi possível gerar o código JS para exportar.');
      return;
    }

    downloadTextFile(`iluminacaoConfig_${today}.js`, jsString + '\n');

    alert(
      `${totalInstances} instâncias exportadas (pontos + fitas LED).\n` +
        `Gerado: iluminacaoConfig_${today}.js`
    );
  };

  // Importar configuração (JS único)
  const handleImportConfig = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = String(e.target.result || '').trim();

        if (!text.startsWith('export const iluminacaoConfig')) {
          throw new Error(
            'Arquivo JS inválido. Esperado: "export const iluminacaoConfig = [...];"'
          );
        }

        const sapConfig = extractConfigFromJsFile(text, 'iluminacaoConfig');

        const editorGroups = sapToEditor(sapConfig);

        setLightPoints(editorGroups);

        const totalInstances = editorGroups.reduce(
          (sum, group) => sum + (group.instances?.length || 0),
          0
        );

        alert(`${totalInstances} instâncias importadas do arquivo JS!`);
      } catch (error) {
        alert('Erro ao importar arquivo JS: ' + error.message);
      }
    };

    reader.readAsText(file);
    event.target.value = '';
  };

  const renderSidebarContent = () => {
    switch (sidebarView) {
      case 'controls':
        return (
          <ControlPanel
            config={currentConfig}
            onConfigChange={handleConfigChange}
            lightPoints={lightPoints}
            onClearAll={handleClearAllLights}
            selectedPoint={selectedPoint}
            onUpdateGroupDescription={handleUpdateGroupDescription}
            onUpdateGroupConfig={handleUpdateGroupConfig}
            onUpdateInstanceConfig={handleUpdateInstanceConfig}
          />
        );
      case 'json':
        return <JsOutput lightPoints={lightPoints} />;
      case 'upload':
        return (
          <ImageUploader
            onImageLoad={handleImageLoad}
            currentImage={currentImage}
          />
        );
      default:
        return null;
    }
  };

  // Totais para o header
  const { totalLights, totalLeds } = useMemo(() => {
    let lights = 0;
    let leds = 0;

    lightPoints.forEach((group) => {
      (group.instances || []).forEach((inst) => {
        if (inst.tipo === 'led') leds += 1;
        else lights += 1;
      });
    });

    return { totalLights: lights, totalLeds: leds };
  }, [lightPoints]);

  return (
    <div className="app">
      <Header
        totalPoints={totalLights}
        totalLeds={totalLeds}
        currentTool={currentConfig.lightType}
        onChangeTool={handleChangeTool}
        onOpenSidebar={openSidebar}
        onExportConfig={handleExportConfig}
        onImportConfig={handleImportConfig}
        currentImage={currentImage}
      />

      <main className="main-content">
        <div className="workspace">
          <div className="main-panel">
            {!currentImage ? (
              <div className="welcome-screen">
                <div className="welcome-content">
                  <h2>Bem-vindo ao CMD TOOLS</h2>
                  <p>
                    Comece carregando uma planta do condomínio para posicionar
                    os pontos de luz ou fitas de LED
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => openSidebar('upload')}
                  >
                    📁 Carregar Planta
                  </button>
                </div>
              </div>
            ) : (
              <PlantCanvas
                imageUrl={currentImage.url}
                lightPoints={getElementsForCanvas()}
                onAddLightPoint={handleAddLightPoint}
                onSelectLightPoint={handleSelectLightPoint}
                onDeleteLightPoint={handleDeleteLightPoint}
                selectedPointId={selectedPointId}
                currentConfig={currentConfig}
                onQuickAdvanceNumber={handleQuickAdvanceNumber}
                onQuickResetNumber={handleQuickResetNumber}
                onQuickNextSlot={handleQuickNextSlot}
              />
            )}
          </div>
        </div>
      </main>

      {/* Sidebar */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
        onClick={closeSidebar}
      ></div>
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>
            {sidebarView === 'controls' && '⚙️ Configurações'}
            {sidebarView === 'json' && '📄 Código JS'}
            {sidebarView === 'upload' && '📁 Carregar Planta'}
          </h3>
          <button className="close-sidebar-btn" onClick={closeSidebar}>
            ✕
          </button>
        </div>
        <div className="sidebar-content">{renderSidebarContent()}</div>
      </div>

      <NameGroupModal
        isOpen={!!pendingGroup}
        defaultName={pendingGroup?.defaultDescricao || ''}
        initialName={pendingGroup?.initialDescricao || ''}
        onConfirm={handleConfirmGroupName}
        onCancel={handleCancelGroupName}
      />

      {/* Input hidden para importar configuração */}
      <input
        type="file"
        accept=".js"
        onChange={handleImportConfig}
        style={{ display: 'none' }}
        id="import-config"
      />
    </div>
  );
};

export default App;